/**
 * /admin/players/import
 *
 * Admin-only page for bulk CSV roster import.
 * - File picker (CSV only)
 * - 10-row preview parsed client-side with papaparse
 * - Submit sends multipart form to POST /api/admin/players/bulk-import
 * - Result summary: created / skipped / errors
 */

import { useState, useRef, ChangeEvent } from "react";
import Papa from "papaparse";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

const REQUIRED_COLS = ["first_name", "last_name", "email", "date_of_birth", "age_group"];
const TEMPLATE_URL = "/templates/roster-template.csv";

type PreviewRow = Record<string, string>;
type ImportResult = {
  ok: boolean;
  created: number;
  skipped: number;
  errors: string[];
  importBatchId?: string;
  error?: string;
};

export default function PlayerImportPage() {
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewRow[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [missingCols, setMissingCols] = useState<string[]>([]);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // ── File selection ──────────────────────────────────────────────────────
  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0];
    if (!picked) return;

    setFile(picked);
    setResult(null);

    Papa.parse<PreviewRow>(picked, {
      header: true,
      skipEmptyLines: true,
      preview: 10,
      transformHeader: (h) => h.trim().toLowerCase().replace(/\s+/g, "_"),
      complete: ({ data, meta }) => {
        const cols = meta.fields ?? [];
        setHeaders(cols);
        setPreview(data);
        setMissingCols(REQUIRED_COLS.filter((c) => !cols.includes(c)));
      },
    });
  }

  // ── Submit ──────────────────────────────────────────────────────────────
  async function handleSubmit() {
    if (!file || missingCols.length) return;
    setSubmitting(true);
    setResult(null);

    try {
      const form = new FormData();
      form.append("roster", file);

      const res = await fetch("/api/admin/players/bulk-import", {
        method: "POST",
        credentials: "include",
        body: form,
      });

      const json: ImportResult = await res.json();
      setResult(json);

      if (json.ok) {
        toast({
          title: "Import complete",
          description: `${json.created} created, ${json.skipped} skipped`,
        });
      } else {
        toast({
          title: "Import failed",
          description: json.error ?? "Unknown error",
          variant: "destructive",
        });
      }
    } catch (err: any) {
      toast({
        title: "Network error",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  }

  // ── Reset ───────────────────────────────────────────────────────────────
  function handleReset() {
    setFile(null);
    setPreview([]);
    setHeaders([]);
    setMissingCols([]);
    setResult(null);
    if (fileRef.current) fileRef.current.value = "";
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Import Roster</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Upload a CSV to bulk-create players and parent accounts.
          </p>
        </div>
        <a
          href={TEMPLATE_URL}
          download
          className="text-sm text-primary underline underline-offset-2"
        >
          Download template
        </a>
      </div>

      {/* ── Required columns callout ── */}
      <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        <strong>Required columns:</strong>{" "}
        {REQUIRED_COLS.join(", ")}
        <br />
        <span className="text-amber-600">
          Optional: team_name, player_type, emergency_contact
        </span>
        <br />
        <span className="mt-1 block">
          The CSV must include <strong>date_of_birth</strong> (YYYY-MM-DD) and{" "}
          <strong>age_group</strong> (e.g. "U11"). Email is used as the parent
          account identifier — duplicate emails within your academy are skipped.
        </span>
      </div>

      {/* ── File picker ── */}
      <div className="flex items-center gap-4">
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          onChange={handleFileChange}
          className="block text-sm text-muted-foreground
            file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0
            file:text-sm file:font-medium file:bg-primary file:text-white
            hover:file:bg-primary/90 cursor-pointer"
          data-testid="csv-file-input"
        />
        {file && (
          <Button variant="ghost" size="sm" onClick={handleReset}>
            Clear
          </Button>
        )}
      </div>

      {/* ── Missing columns error ── */}
      {missingCols.length > 0 && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          Missing required columns: <strong>{missingCols.join(", ")}</strong>
        </div>
      )}

      {/* ── Preview table ── */}
      {preview.length > 0 && (
        <div className="overflow-x-auto rounded-md border">
          <table className="min-w-full text-xs">
            <thead className="bg-muted">
              <tr>
                {headers.map((h) => (
                  <th
                    key={h}
                    className={`px-3 py-2 text-left font-medium ${
                      REQUIRED_COLS.includes(h)
                        ? "text-foreground"
                        : "text-muted-foreground"
                    }`}
                  >
                    {h}
                    {REQUIRED_COLS.includes(h) && (
                      <span className="ml-1 text-primary">*</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.map((row, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-background" : "bg-muted/30"}>
                  {headers.map((h) => (
                    <td key={h} className="px-3 py-1.5 text-muted-foreground">
                      {row[h] ?? ""}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-3 py-2 text-xs text-muted-foreground border-t">
            Showing first {preview.length} row{preview.length !== 1 ? "s" : ""} — full file will be imported.
          </p>
        </div>
      )}

      {/* ── Submit ── */}
      {file && missingCols.length === 0 && !result && (
        <Button
          onClick={handleSubmit}
          disabled={submitting}
          data-testid="import-submit"
        >
          {submitting ? "Importing…" : "Import Roster"}
        </Button>
      )}

      {/* ── Result summary ── */}
      {result && (
        <div
          data-testid="import-result"
          className={`rounded-md border p-4 space-y-2 text-sm ${
            result.ok
              ? "border-green-200 bg-green-50 text-green-900"
              : "border-destructive/50 bg-destructive/10 text-destructive"
          }`}
        >
          {result.ok ? (
            <>
              <p className="font-semibold">Import complete</p>
              <p>
                <strong>{result.created}</strong> player
                {result.created !== 1 ? "s" : ""} created &nbsp;·&nbsp;{" "}
                <strong>{result.skipped}</strong> skipped (duplicate email)
              </p>
              {result.importBatchId && (
                <p className="text-xs text-green-700">
                  Batch ID: {result.importBatchId}
                </p>
              )}
            </>
          ) : (
            <p className="font-semibold">{result.error}</p>
          )}
          {result.errors?.length > 0 && (
            <ul className="list-disc list-inside space-y-0.5 text-xs text-amber-700">
              {result.errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          )}
          <Button variant="outline" size="sm" onClick={handleReset}>
            Import another file
          </Button>
        </div>
      )}
    </div>
  );
}
